import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Collapse, Divider, Drawer,
    FormControl, IconButton, InputAdornment, Menu, MenuItem,
    Paper, Select, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import Layout from './Layout';
import PageHeader from './PageHeader';
import ServiceService from '../services/ServiceService';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';

const UNITS = ['hours', 'pieces', 'm', 'm2', 'kg'];

const BRAND = '#427898';

/* ── Reusable WorkspaceDrawer (same pattern as CustomerInfo) ─────────────── */
const WorkspaceDrawer = ({ open, onClose, title, subtitle, saveLabel, onSave, saving, width = 540, children }) => (
    <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: width }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 3, bgcolor: BRAND, flexShrink: 0 }} />
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
                    {subtitle && <Typography fontSize={12} color="#94A3B8" mt={0.3}>{subtitle}</Typography>}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                {children}
            </Box>
            <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving}
                    sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                    {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : saveLabel}
                </Button>
            </Box>
        </Box>
    </Drawer>
);

/* ── Service card ────────────────────────────────────────────────────────── */
function ServiceCard({ service, onEdit, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const { t } = useTranslation();

    const subs = service.subServices || [];

    return (
        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', transition: 'box-shadow 0.15s', '&:hover': { boxShadow: '0 2px 12px rgba(15,23,42,0.06)' } }}>
            {/* Header row */}
            <Box
                sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.75, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#FAFBFC' } }}
                onClick={() => setExpanded(e => !e)}
            >
                <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: '#EEF6FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 1.75 }}>
                    <CategoryIcon sx={{ fontSize: 17, color: BRAND }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} fontSize={14} color="#0F172A" noWrap>{service.name}</Typography>
                    <Typography fontSize={11} color="#94A3B8" mt={0.1}>
                        {subs.length === 0 ? 'No subservices' : `${subs.length} subservice${subs.length !== 1 ? 's' : ''}`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
                    <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                        sx={{ color: '#CBD5E1', borderRadius: 1, '&:hover': { bgcolor: '#F1F5F9', color: '#475569' } }}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#CBD5E1', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        <ExpandMoreIcon fontSize="small" />
                    </Box>
                </Box>
            </Box>

            {/* Subservices panel */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider />
                {subs.length === 0 ? (
                    <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
                        <Typography fontSize={13} color="#94A3B8">No subservices yet.</Typography>
                        <Button size="small" onClick={e => { e.stopPropagation(); onEdit(service); }}
                            sx={{ mt: 1, textTransform: 'none', fontSize: 12, color: BRAND, fontWeight: 600 }}>
                            + Add subservices
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 72px 96px', gap: 2, px: 1, mb: 1 }}>
                            {['Name', 'Unit', 'Price (CHF)'].map(h => (
                                <Typography key={h} fontSize={10} fontWeight={600} color="#CBD5E1" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{h}</Typography>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {subs.map((sub, i) => (
                                <Box key={sub.id ?? i} sx={{ display: 'grid', gridTemplateColumns: '1fr 72px 96px', gap: 2, px: 1, py: 0.75, borderRadius: 1, '&:hover': { bgcolor: '#F8FAFC' } }}>
                                    <Typography fontSize={13} color="#334155" noWrap>{sub.name}</Typography>
                                    <Chip
                                        label={t(`services.units.${sub.unit}`, sub.unit)}
                                        size="small"
                                        sx={{ fontSize: 11, height: 20, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 500, width: 'fit-content', borderRadius: '6px' }}
                                    />
                                    <Typography fontSize={13} color="#334155" fontWeight={500}>
                                        {Number(sub.price).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Collapse>

            {/* Context menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{ elevation: 0, sx: { border: '1px solid #EEF2F6', borderRadius: 1.5, boxShadow: '0 4px 20px rgba(15,23,42,0.10)', minWidth: 148 } }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { onEdit(service); setMenuAnchor(null); }}
                    sx={{ fontSize: 13, gap: 1.5, color: '#334155', py: 1 }}>
                    <EditOutlinedIcon fontSize="small" sx={{ color: '#64748B' }} />
                    Edit
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={() => { onDelete(service); setMenuAnchor(null); }}
                    sx={{ fontSize: 13, gap: 1.5, color: '#EF4444', py: 1 }}>
                    <DeleteOutlineIcon fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>
        </Paper>
    );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
const ServicesPage = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();

    const [services, setServices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const [drawerMode, setDrawerMode] = useState(null); // 'create' | 'edit'
    const [drawerService, setDrawerService] = useState(null);
    const [draftName, setDraftName] = useState('');
    const [draftSubs, setDraftSubs] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const toastData = localStorage.getItem('TOAST_MESSAGE');
        if (toastData) {
            const { message, type } = JSON.parse(toastData);
            showToast(message, type);
            localStorage.removeItem('TOAST_MESSAGE');
        }
    }, [showToast]);

    useEffect(() => {
        ServiceService.getAllServices()
            .then(data => setServices(data.content || []))
            .catch(console.error);
    }, []);

    const filtered = searchTerm.trim()
        ? services.filter(s => s.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
        : services;

    /* ── Drawer open/close ── */
    const openCreate = () => {
        setDraftName('');
        setDraftSubs([]);
        setDrawerService(null);
        setDrawerMode('create');
    };

    const openEdit = async (service) => {
        try {
            const full = await ServiceService.getService(service.id);
            setDraftName(full.name);
            setDraftSubs(full.subServices ?? []);
            setDrawerService(full);
            setDrawerMode('edit');
        } catch {
            showToast('Failed to load service', 'error');
        }
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        setDrawerService(null);
        setDraftName('');
        setDraftSubs([]);
    };

    /* ── Subservice draft actions ── */
    const addSub = () => {
        setDraftSubs(prev => [...prev, { id: null, tempId: `tmp-${Date.now()}`, name: '', unit: 'hours', price: 0, used: false }]);
    };

    const changeSub = (index, field, value) => {
        setDraftSubs(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const removeSub = (index) => {
        setDraftSubs(prev => prev.filter((_, i) => i !== index));
    };

    /* ── Save ── */
    const handleSave = async () => {
        if (!draftName.trim()) {
            showToast(t('services.toasts.nameRequired'), 'error');
            return;
        }
        setSaving(true);
        try {
            const dto = {
                name: draftName.trim(),
                subServices: draftSubs
                    .filter(s => s.name?.trim())
                    .map(s => ({
                        id: Number.isInteger(s.id) ? s.id : null,
                        name: s.name.trim(),
                        unit: s.unit || 'hours',
                        price: parseFloat(s.price) || 0,
                    }))
            };
            if (drawerMode === 'create') {
                const created = await ServiceService.createService(dto);
                setServices(prev => [...prev, created]);
                showToast(t('services.toasts.createdSuccess'), 'success');
            } else {
                const updated = await ServiceService.updateService(drawerService.id, { ...dto, id: drawerService.id });
                setServices(prev => prev.map(s => s.id === updated.id ? updated : s));
                showToast('Service updated successfully!', 'success');
            }
            closeDrawer();
        } catch (err) {
            showToast(err?.response?.data?.message || t('services.toasts.createError'), 'error');
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete ── */
    const handleDelete = async (service) => {
        try {
            await ServiceService.deleteService(service.id);
            setServices(prev => prev.filter(s => s.id !== service.id));
            showToast(t('services.toasts.deletedSuccess'), 'warning');
        } catch (err) {
            showToast(err?.response?.data?.message || t('services.toasts.deleteError'), 'error');
        }
    };

    const subCount = draftSubs.length;

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <PageHeader
                    title={t('services.title')}
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openCreate}
                            sx={{ bgcolor: BRAND, borderRadius: 1.5, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                        >
                            {t('services.addNew')}
                        </Button>
                    }
                />

                {/* Search */}
                <Box sx={{ mb: 3, maxWidth: 360 }}>
                    <TextField
                        placeholder={t('services.search')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 17, color: '#CBD5E1' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 1.5 } }}
                    />
                </Box>

                {/* Empty state */}
                {filtered.length === 0 && (
                    <Paper elevation={0} sx={{ border: '1.5px dashed #E2E8F0', borderRadius: 2, p: 6, textAlign: 'center', bgcolor: '#FAFBFC' }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: '#EEF6FB', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                            <CategoryIcon sx={{ fontSize: 26, color: '#A8C8DA' }} />
                        </Box>
                        <Typography fontWeight={600} fontSize={14} color="#64748B" mb={0.5}>
                            {searchTerm ? 'No services match your search' : 'No services yet'}
                        </Typography>
                        <Typography fontSize={13} color="#94A3B8" mb={2.5}>
                            {searchTerm ? 'Try a different term' : 'Create your first service to start organising your work'}
                        </Typography>
                        {!searchTerm && (
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}
                                sx={{ textTransform: 'none', fontSize: 13, borderColor: BRAND, color: BRAND, borderRadius: 1.5 }}>
                                Add Service
                            </Button>
                        )}
                    </Paper>
                )}

                {/* Service list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {filtered.map(service => (
                        <ServiceCard
                            key={service.id}
                            service={service}
                            onEdit={openEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </Box>
            </Box>

            {/* Create / Edit drawer */}
            <WorkspaceDrawer
                open={Boolean(drawerMode)}
                onClose={closeDrawer}
                title={drawerMode === 'create' ? 'New Service' : `Edit Service`}
                subtitle={drawerMode === 'edit' && drawerService ? drawerService.name : undefined}
                saveLabel={drawerMode === 'create' ? 'Create Service' : 'Save Changes'}
                onSave={handleSave}
                saving={saving}
                width={540}
            >
                {/* Service name */}
                <Box sx={{ mb: 3 }}>
                    <Typography fontSize={11} fontWeight={600} color="#94A3B8" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>
                        Service Name
                    </Typography>
                    <TextField
                        placeholder="e.g. Electrical Installation"
                        fullWidth
                        size="small"
                        value={draftName}
                        onChange={e => setDraftName(e.target.value)}
                        autoFocus
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Subservices header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: subCount > 0 ? 1.5 : 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontSize={13} fontWeight={600} color="#0F172A">Subservices</Typography>
                        {subCount > 0 && (
                            <Chip label={subCount} size="small"
                                sx={{ height: 18, fontSize: 11, bgcolor: '#EEF6FB', color: BRAND, fontWeight: 600, '& .MuiChip-label': { px: 1 } }} />
                        )}
                    </Box>
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />} onClick={addSub}
                        sx={{ textTransform: 'none', fontSize: 12, color: BRAND, fontWeight: 600, borderRadius: 1.5, px: 1.5, '&:hover': { bgcolor: '#EEF6FB' } }}>
                        Add
                    </Button>
                </Box>

                {/* Subservices empty */}
                {subCount === 0 && (
                    <Box sx={{ py: 3.5, textAlign: 'center', border: '1.5px dashed #E2E8F0', borderRadius: 1.5 }}>
                        <Typography fontSize={13} color="#94A3B8">No subservices added yet</Typography>
                    </Box>
                )}

                {/* Subservices list */}
                {subCount > 0 && (
                    <Box>
                        {/* Column labels */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 96px 84px 28px', gap: 1.5, mb: 1, px: 0.5 }}>
                            {['Name', 'Unit', 'CHF / unit', ''].map((h, i) => (
                                <Typography key={i} fontSize={10} fontWeight={600} color="#CBD5E1" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {draftSubs.map((sub, index) => {
                                const inUse = Boolean(sub.id) && sub.used === true;
                                return (
                                    <Box key={sub.id ?? sub.tempId ?? index}
                                        sx={{ display: 'grid', gridTemplateColumns: '1fr 96px 84px 28px', gap: 1.5, alignItems: 'center',
                                            p: 1, borderRadius: 1.5,
                                            bgcolor: inUse ? '#F8FAFC' : 'transparent',
                                            border: '1px solid', borderColor: '#EEF2F6',
                                        }}>
                                        <TextField
                                            size="small"
                                            disabled={inUse}
                                            value={sub.name}
                                            onChange={e => changeSub(index, 'name', e.target.value)}
                                            placeholder="Name"
                                            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1 }, '& fieldset': { borderColor: '#E2E8F0' } }}
                                        />
                                        <Select
                                            size="small"
                                            disabled={inUse}
                                            value={sub.unit || 'hours'}
                                            onChange={e => changeSub(index, 'unit', e.target.value)}
                                            sx={{ fontSize: 13, borderRadius: 1, '& fieldset': { borderColor: '#E2E8F0' } }}
                                        >
                                            {UNITS.map(u => (
                                                <MenuItem key={u} value={u} sx={{ fontSize: 13 }}>{t(`services.units.${u}`, u)}</MenuItem>
                                            ))}
                                        </Select>
                                        <TextField
                                            size="small"
                                            type="number"
                                            disabled={inUse}
                                            value={sub.price}
                                            onChange={e => changeSub(index, 'price', e.target.value)}
                                            inputProps={{ min: 0, step: '0.01' }}
                                            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1 }, '& fieldset': { borderColor: '#E2E8F0' }, '& input': { fontSize: 13 } }}
                                        />
                                        {inUse ? (
                                            <Tooltip title="In use — cannot delete" arrow placement="left">
                                                <InfoOutlinedIcon sx={{ fontSize: 15, color: '#CBD5E1' }} />
                                            </Tooltip>
                                        ) : (
                                            <IconButton size="small" onClick={() => removeSub(index)}
                                                sx={{ color: '#CBD5E1', p: 0.25, '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                                                <CloseIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </WorkspaceDrawer>
        </Layout>
    );
};

export default ServicesPage;
